import 'typeface-roboto';
import './styles/App.css';
import './styles/LibOverride.css';

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import React, { Suspense, useEffect, useState } from 'react';

import AppToast from '../AppToast';
import Assessment from '../assessments/App';
import AuthService from '../shared/services/AuthService';
import { AvatarContextProvider } from '../membership/hooks/AvatarContext';
import Bugsnag from '@bugsnag/js';
import Dashboard from '../dashboard/App';
import DescriptionIcon from '@mui/icons-material/Description';
import Feature from '../shared/models/Feature';
import FeatureAccessService from '../shared/services/FeatureAccessService';
import Footer from './components/Footer';
import Header from './components/Header';
import HtmlPageService from '../shared/services/HtmlPageService';
import InvertColorsSharpIcon from '@mui/icons-material/InvertColorsSharp';
import { Notice } from '../notifications/models/Notice';
import { NoticeReceiver } from '../notifications/models/NoticeReceiver';
import Notifications from '../notifications/App';
import { Page404 } from '../shared/components/Page404';
import { Quill } from 'react-quill';
import ReactDOMServer from 'react-dom/server';
import { SWRConfig } from 'swr';
import { SkinThemeProvider } from '../shared/SkinThemeContext';
import SkinThemeService from '../shared/services/SkinThemeService';
import SplashScreen from './components/SplashScreen';
import { ThemeProvider } from '@mui/material/styles';
import User from '../membership/models/User';
import { UserGroupSelection } from '../dashboard/components/UserGroupSelection';
import UserProfileRequiredDialog from '../dashboard/components/UserProfileRequiredDialog';
import axios from 'axios';
import useSingletonUser from '../shared/hooks/useSingletonUser';
import useThemes from '../shared/hooks/useThemes';
import useUserAvatar from '../membership/hooks/useUserAvatar';

const ReportComponent = React.lazy(() => import('../report/App'));
const GroupComponent = React.lazy(() => import('../group/App'));
const DesignComponent = React.lazy(() => import('../design/App'));
const LinkedAccounts = React.lazy(() => import('../user/App'));
const SiteAdmin = React.lazy(() => import('../siteadmin/App'));
const CourseCatalog = React.lazy(() => import('../group/CourseCatalog'));
const Content = React.lazy(() => import('../content/App'));
const Resources = React.lazy(() => import('../resources/App'));

/* This function is used in the InlineQuillEdit.tsx for customizing quill toolbar functionalities. 
This should be called only once so placed here at the top level  */

//customizing the color icon color in the quill editor toolbar
const icons = Quill.import('ui/icons');

icons['customColor'] = ReactDOMServer.renderToString(
  <InvertColorsSharpIcon className={'toolbar-icon-color'} />
);

icons['customLink'] = ReactDOMServer.renderToString(
  <DescriptionIcon fontSize="small" className={'toolbar-icon-color'} />
);

//customizing the link input
const link = Quill.import('formats/link');
const builtInFunc = link.sanitize;

link.sanitize = function customSanitizeLinkInput(linkValueInput: any) {
  let val = linkValueInput;

  /* Adds 'https://' to the val if the user doesn't add any protocol.
  eg. google.com will be https://google.com */
  if (!/^\w+:/.test(val)) {
    val = 'https://' + val;
  }

  return builtInFunc.call(this, val); // retain the built-in logic
};
/***************/

const App = () => {
  const [currentUser, setCurrentUser] = useState<User>(null);
  const [menuReady, setMenuReady] = useState(false);
  const [requiredProfileChecked, setRequiredProfileChecked] = useState(false);
  const [userGroupSelection, setUserGroupSelection] =
    useState<boolean>(undefined);
  const [features, setFeatures] = useState<Feature[]>(null);
  const [notificationsReceived, setNotificationsReceived] = useState<
    NoticeReceiver[]
  >([]);
  const [notificationsSent, setNotificationsSent] = useState<Notice[]>([]);

  const location = useLocation();
  const navigate = useNavigate();
  const { muiTheme, skinTheme } = useThemes();

  const avatarContextProvider = useUserAvatar(currentUser);

  // Determine if the current path is /design/mui
  const isDesignMuiRoute = location.pathname.startsWith('/design/mui');

  //Get loggedin user from singleton user hook
  const { user } = useSingletonUser();

  //Set currentUser as loggedin user
  useEffect(() => {
    if (user) {
      setCurrentUser(user);
    }
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/' && features) {
      const path = FeatureAccessService.getLandingPath();
      navigate(path);
    }
  }, [features]);

  useEffect(() => {
    if (currentUser) {
      const { userkey } = currentUser;
      Bugsnag.setUser(`${userkey}`);
      Bugsnag.setContext(AuthService.Tenant);
      FeatureAccessService.initAsync(currentUser).then(() => {
        setMenuReady(true);
        setFeatures(FeatureAccessService.AvailableFeatures);
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (!skinTheme) {
      return;
    }
    HtmlPageService.setFavicon(
      SkinThemeService.getAssetUrl('container', 'favicon')
    );
    HtmlPageService.setTitle(
      SkinThemeService.getThemeOptionValue(skinTheme, 'container', 'siteName')
    );
  }, [skinTheme]);

  const handleNotificationsUpdate = (
    receivedNotifications: NoticeReceiver[],
    sentNotifications: Notice[]
  ) => {
    setNotificationsReceived(receivedNotifications);
    setNotificationsSent(sentNotifications);
  };

  return currentUser && menuReady && !!muiTheme && !!skinTheme ? (
    <ThemeProvider theme={muiTheme}>
      <SkinThemeProvider value={skinTheme}>
        <AvatarContextProvider value={avatarContextProvider}>
          <SWRConfig
            value={{
              fetcher: (url: string) =>
                axios(url, {
                  headers: {
                    Authorization: `Bearer ${AuthService.getAccessToken()}`
                  }
                }),
              revalidateOnFocus: false //To prevent re-rendering on focus (eg.CourseDialog component where useSWR used)
            }}
          >
            <div className="portal-header">
              <Header
                user={currentUser}
                features={features}
                onNotificationsUpdate={handleNotificationsUpdate}
              />
            </div>
            <Suspense fallback={<div>Loading...</div>}>
              {userGroupSelection === false && (
                <div className="App">
                  <div
                    className="portal-body"
                    style={{ overflow: isDesignMuiRoute ? 'hidden' : 'auto' }}
                  >
                    <Routes>
                      <Route
                        index
                        element={<Navigate to="/dashboard/course" />}
                      />
                      <Route
                        path={'dashboard/*'}
                        element={<Dashboard user={currentUser} />}
                      />
                      <Route path="/resources/*" element={<Resources />} />
                      <Route
                        path="/linkedaccounts"
                        element={<LinkedAccounts />}
                      />
                      <Route
                        path="/assessments/*"
                        element={<Assessment user={currentUser} />}
                      />
                      <Route
                        path="/reporting/*"
                        element={<ReportComponent />}
                      />
                      <Route path="/usergroup/*" element={<GroupComponent />} />
                      <Route path="/content/*" element={<Content />} />
                      <Route path="/design/*" element={<DesignComponent />} />
                      <Route path="/siteadmin/*" element={<SiteAdmin />} />
                      <Route
                        path="/notifications/*"
                        element={
                          <Notifications
                            receivedNotifications={notificationsReceived}
                            sentNotifications={notificationsSent}
                          />
                        }
                      />
                      {/* Fallback route for 404 page */}
                      <Route path="*" element={<Page404 />} />
                    </Routes>
                  </div>
                </div>
              )}
            </Suspense>
            <div className="portal-footer">
              <Footer copyrightStartYear={2019} />
            </div>
            <UserGroupSelection
              open={userGroupSelection}
              setDialogOpen={setUserGroupSelection}
            />
            {!requiredProfileChecked && (
              <UserProfileRequiredDialog
                userkey={+currentUser.userkey}
                open={!requiredProfileChecked}
                onClose={() => {
                  setRequiredProfileChecked(true);
                }}
              />
            )}
          </SWRConfig>
        </AvatarContextProvider>
      </SkinThemeProvider>
      <AppToast />
    </ThemeProvider>
  ) : (
    <SplashScreen />
  );
};

export default App;
